import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { trpc } from '@/providers/trpc';
import { trackSignupComplete } from '@/lib/analytics';

interface User {
  id: number;
  email: string;
  name: string;
  company?: string | null;
  plan: string;
  lookupsUsed: number;
  lookupsLimit: number;
  role: string;
  onboardingComplete: boolean;
  emailVerified?: boolean | null;
  lookupsRemaining?: number | null;
  trialStatus?: string | null;
  trialEndsAt?: string | null;
  daysLeft?: number | null;
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name?: string, turnstileToken?: string) => Promise<{ success: boolean; error?: string }>;
  googleLogin: (code: string, state: string) => Promise<{ success: boolean; error?: string }>;
  appleLogin: (code: string, idToken: string, state: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoggedIn: false,
  isLoading: true,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  googleLogin: async () => ({ success: false }),
  appleLogin: async () => ({ success: false }),
  logout: () => {},
  isAdmin: false,
});

// Detect demo mode from URL — skip auth API calls when in demo
const isDemoMode = () => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('demo') === 'true';
};

// Check if session cookie exists — only call auth.me if user likely has a session
const hasSessionCookie = () => {
  if (typeof document === 'undefined') return false;
  return document.cookie.includes('plp_session');
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const demoMode = isDemoMode();

  // Always call auth.me — the HttpOnly session cookie is invisible to JS,
  // so we can't gate the query on cookie presence.
  // auth.me returns 401 for logged-out users; the useEffect below handles that.
  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !demoMode,
  });

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      if (data.success && data.user) {
        setUser(data.user as User);
        utils.auth.me.invalidate();
      }
    },
  });

  const googleCallbackMutation = trpc.auth.googleOAuthCallback.useMutation({
    onSuccess: (data) => {
      if (data?.success && data.user) {
        setUser(data.user as User);
        utils.auth.me.invalidate();
      }
    },
  });

  const appleCallbackMutation = trpc.auth.appleSignIn.useMutation({
    onSuccess: (data) => {
      if (data?.success && data.user) {
        setUser(data.user as User);
        utils.auth.me.invalidate();
      }
    },
  });

  const registerMutation = trpc.auth.register.useMutation({
    onSuccess: (data) => {
      // Registration successful - track and auto-login
      trackSignupComplete('email');
    },
  });

  // Get query utils for cache invalidation
  const utils = trpc.useUtils();

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      setUser(null);
      // httpOnly cookie cleared server-side — no client cleanup needed
    },
  });

  // Sync tRPC query result with local state
  useEffect(() => {
    if (demoMode) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    if (meQuery.isSuccess) {
      // auth.me returns User | null directly (not { user: ... })
      const userData = meQuery.data ?? null;
      setUser(userData);
      setIsLoading(false);
    } else if (meQuery.isError) {
      setUser(null);
      setIsLoading(false);
    }
    // Note: no "else" — auth.me is always enabled, so it will resolve
  }, [meQuery.data, meQuery.isSuccess, meQuery.isError, demoMode]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await loginMutation.mutateAsync({ email, password });
      if (result.success && result.user) {
        setUser(result.user as User);
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (e: any) {
      return { success: false, error: e.message || 'Invalid email or password' };
    }
  }, [loginMutation]);

  const register = useCallback(async (email: string, password: string, name?: string, turnstileToken?: string) => {
    try {
      console.log('[Auth] Registering user:', { email, name, hasToken: !!turnstileToken });
      const result = await registerMutation.mutateAsync({ email, password, name, turnstileToken });
      console.log('[Auth] Registration result:', result);
      // Auto-login: set user from registration response and invalidate auth.me
      if (result.success && result.user) {
        setUser(result.user as User);
        // Invalidate auth.me so it refetches with the new session cookie
        utils.auth.me.invalidate();
        return { success: true, user: result.user };
      }
      return { success: true };
    } catch (e: any) {
      console.error('[Auth] Registration error:', e);
      return { success: false, error: e.message || 'Registration failed' };
    }
  }, [registerMutation, utils]);

  const logout = useCallback(() => {
    logoutMutation.mutate();
    setUser(null);
  }, [logoutMutation]);

  const googleLogin = useCallback(async (code: string, state: string) => {
    try {
      const result = await googleCallbackMutation.mutateAsync({ code, state });
      if (result?.success) {
        return { success: true };
      }
      return { success: false, error: 'Google sign-in was not successful' };
    } catch (e: any) {
      console.error('[Auth] Google login error:', e);
      return { success: false, error: e.message || 'Google sign-in failed' };
    }
  }, [googleCallbackMutation]);

  const appleLogin = useCallback(async (code: string, idToken: string, state: string) => {
    try {
      const result = await appleCallbackMutation.mutateAsync({ code, idToken, state });
      if (result?.success) {
        return { success: true };
      }
      return { success: false, error: 'Apple sign-in was not successful' };
    } catch (e: any) {
      console.error('[Auth] Apple login error:', e);
      return { success: false, error: e.message || 'Apple sign-in failed' };
    }
  }, [appleCallbackMutation]);

  const value: AuthContextType = {
    user,
    isLoggedIn: !!user,
    isLoading,
    login,
    register,
    googleLogin,
    appleLogin,
    logout,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
